/**
 * Deal Box Upload Dialog Component
 * File upload form with document type, category, visibility selection
 */

import { useState, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealBox } from '@/hooks/useDealBox';
import { 
  DEAL_BOX_TRANSLATIONS, 
  DOCUMENT_TYPE_LABELS, 
  CATEGORY_LABELS,
  ROLE_LABELS,
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE
} from '@/constants/deal-box.constants';
import type { DealFile, DocumentType, DocumentCategory, VisibilityRole } from '@/types/deal-box.types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileUp, X, AlertCircle } from 'lucide-react';

interface DealBoxUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  replaceFile?: DealFile | null;
  onSuccess: () => void;
}

export function DealBoxUploadDialog({
  open,
  onOpenChange,
  dealId,
  replaceFile,
  onSuccess,
}: DealBoxUploadDialogProps) {
  const { language } = useLanguage();
  const t = DEAL_BOX_TRANSLATIONS[language as 'en' | 'fr'] || DEAL_BOX_TRANSLATIONS.en;
  const docTypeLabels = DOCUMENT_TYPE_LABELS[language as 'en' | 'fr'] || DOCUMENT_TYPE_LABELS.en;
  const categoryLabels = CATEGORY_LABELS[language as 'en' | 'fr'] || CATEGORY_LABELS.en;
  const roleLabels = ROLE_LABELS[language as 'en' | 'fr'] || ROLE_LABELS.en;

  const { uploadFile, uploadProgress, loading, error } = useDealBox(dealId);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState(replaceFile?.title || '');
  const [description, setDescription] = useState(replaceFile?.description || '');
  const [documentType, setDocumentType] = useState<DocumentType>(replaceFile?.documentType || 'other');
  const [category, setCategory] = useState<DocumentCategory>(replaceFile?.category || 'misc');
  const [visibilityRoles, setVisibilityRoles] = useState<VisibilityRole[]>(
    replaceFile?.visibilityRoles || ['seller', 'buyer', 'broker', 'staff', 'admin']
  );
  const [dragOver, setDragOver] = useState(false);

  // Document type options
  const documentTypeOptions: DocumentType[] = [
    'nda_signed', 'loi', 'ioi', 'pnl_annual', 'balance_sheet', 
    'tax_report', 'cim', 'evaluation', 'contract', 'license', 'other'
  ];

  // Category options
  const categoryOptions: DocumentCategory[] = [
    'financials', 'legal', 'cim', 'licenses', 'evaluations', 'misc'
  ];

  // Visibility role options
  const visibilityOptions: VisibilityRole[] = [
    'seller', 'buyer', 'broker', 'lender', 'consultant', 'partner', 'staff', 'admin'
  ];

  // Handle file selection
  const handleFileSelect = useCallback((selectedFile: File) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error(language === 'fr' 
        ? 'Le fichier est trop volumineux (max 200MB)' 
        : 'File is too large (max 200MB)'
      );
      return;
    }
    setFile(selectedFile);
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  }, [language, title]);

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  // Toggle visibility role
  const toggleVisibility = (role: VisibilityRole) => {
    if (visibilityRoles.includes(role)) {
      setVisibilityRoles(visibilityRoles.filter(r => r !== role));
    } else {
      setVisibilityRoles([...visibilityRoles, role]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!file) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un fichier' : 'Please select a file');
      return;
    }

    if (!title.trim()) {
      toast.error(language === 'fr' ? 'Veuillez entrer un titre' : 'Please enter a title');
      return;
    }

    if (visibilityRoles.length === 0) {
      toast.error(language === 'fr' 
        ? 'Veuillez sélectionner au moins un rôle de visibilité' 
        : 'Please select at least one visibility role'
      );
      return;
    }

    const result = await uploadFile({
      dealId,
      file,
      documentType,
      category,
      title: title.trim(),
      description: description.trim() || undefined,
      visibilityRoles,
      replaceFileId: replaceFile?.id,
    });

    if (result) {
      onSuccess();
      // Reset form
      setFile(null);
      setTitle('');
      setDescription('');
      setDocumentType('other');
      setCategory('misc');
      setVisibilityRoles(['seller', 'buyer', 'broker', 'staff', 'admin']);
    } else {
      toast.error(t.uploadError);
    }
  };

  // Reset on close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFile(null);
      setTitle(replaceFile?.title || '');
      setDescription(replaceFile?.description || '');
      setDocumentType(replaceFile?.documentType || 'other');
      setCategory(replaceFile?.category || 'misc');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {replaceFile ? t.replaceFile : t.uploadNew}
          </DialogTitle>
          <DialogDescription>
            {replaceFile 
              ? `${language === 'fr' ? 'Nouvelle version de' : 'New version of'} "${replaceFile.title}"`
              : t.subtitle
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
              ${file ? 'bg-accent/50' : ''}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              className="hidden"
              accept={Object.values(ACCEPTED_FILE_TYPES).join(',')}
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileUp className="h-8 w-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Glissez-déposez un fichier ou cliquez pour sélectionner'
                    : 'Drag and drop a file or click to select'
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, Word, Excel, Images, ZIP (max 200MB)
                </p>
              </>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t.enterTitle} *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.enterTitle}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t.enterDescription}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.enterDescription}
              rows={2}
            />
          </div>

          {/* Document Type & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.selectDocumentType}</Label>
              <Select value={documentType} onValueChange={(v) => setDocumentType(v as DocumentType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypeOptions.map(type => (
                    <SelectItem key={type} value={type}>
                      {docTypeLabels[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t.selectCategory}</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as DocumentCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {categoryLabels[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Visibility Roles */}
          <div className="space-y-2">
            <Label>{t.selectVisibility}</Label>
            <div className="grid grid-cols-2 gap-2">
              {visibilityOptions.map(role => (
                <div key={role} className="flex items-center space-x-2">
                  <Checkbox
                    id={`visibility-${role}`}
                    checked={visibilityRoles.includes(role)}
                    onCheckedChange={() => toggleVisibility(role)}
                  />
                  <label
                    htmlFor={`visibility-${role}`}
                    className="text-sm cursor-pointer"
                  >
                    {roleLabels[role]}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="space-y-2">
              <Progress value={uploadProgress} />
              <p className="text-sm text-center text-muted-foreground">
                {t.uploading} {Math.round(uploadProgress)}%
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button onClick={handleUpload} disabled={loading || !file}>
            {loading ? t.uploading : t.uploadFile}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
